import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useOutletContext } from 'react-router-dom';
import { List, ChevronLeft, X, ShieldCheck, AlertCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import '../../styles/threat-map.css';
import type { NationThreat } from '../../types/dashboard';
import { fetchNationAttacks } from '../../api/status';
import { normalizeCountry } from '../../utils/country';
import countriesGeoJsonRaw from '../../assets/countries-fixed.json';
const countriesGeoJson = countriesGeoJsonRaw as any;

const fallbackCoords: Record<string, [number, number]> = {
  "Hong Kong": [22.3193, 114.1694],
  "Singapore": [1.3521, 103.8198],
  "Taiwan": [23.6978, 120.9605],
  "Macau": [22.1987, 113.5439],
  "Palestine": [31.9522, 35.2332],
  "Vatican City": [41.9029, 12.4534],
};

const getCountryMarkerColor = (count: number, maxCount: number) => {
  if (maxCount <= 0) return '#3b82f6';
  if (count >= Math.max(1, Math.ceil(maxCount * 0.6))) return '#ef4444';
  if (count >= Math.max(1, Math.ceil(maxCount * 0.2))) return '#fbbf24';
  return '#3b82f6';
};

const getActorColor = (actor?: string) => {
  const act = (actor || '').toLowerCase();
  if (act.includes('lockbit')) return '#ef4444'; 
  if (act.includes('clop')) return '#8b5cf6'; 
  if (act.includes('blackbasta') || act.includes('basta')) return '#fbbf24'; 
  if (act.includes('play')) return '#f97316'; 
  return '#00bcf2';
};

interface ThreatMapProps {
  nations?: NationThreat[];
  timeframe?: string;
  loading?: boolean;
}

export default function ThreatMap({ nations = [], timeframe = "30d", loading = false }: ThreatMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<L.Map | null>(null);
  const geoJsonLayerRef = useRef<L.LayerGroup | null>(null);
  const markersGroupRef = useRef<L.FeatureGroup | null>(null);

  const { theme } = useOutletContext<{ theme: "dark" | "light"; toggleTheme: () => void }>();
  const isDark = theme === 'dark';

  const [geoJsonCenters, setGeoJsonCenters] = useState<Record<string, [number, number]>>({});
  const [selectedCountryInfo, setSelectedCountryInfo] = useState<{ 
    countryName: string; 
    list: any[]; 
    loading?: boolean; 
    error?: boolean; 
  } | null>(null);
  const [selectedAttack, setSelectedAttack] = useState<any | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const clickHandlerRef = useRef<(countryName: string) => void>(() => {});

  const maxCount = useMemo(() => Math.max(1, ...nations.map((n) => n.count)), [nations]);
  const highThreshold = Math.max(1, Math.ceil(maxCount * 0.6));
  const medThreshold = Math.max(1, Math.ceil(maxCount * 0.2));
  const totalAttacks = nations.reduce((a, b) => a + b.count, 0) || 1;

  // Memoize nations data for fast O(1) lookup
  const nationMap = useMemo(() => {
    const map = new Map<string, NationThreat>();
    for (const n of nations) {
      const norm = normalizeCountry(n.country, n.code);
      const entry: NationThreat = {
        ...n,
        country: norm.name,
        code: norm.iso2,
        flag: n.flag && n.flag !== "🌐" ? n.flag : norm.flag,
      };

      if (norm.numericId && norm.numericId !== "000") {
        map.set(norm.numericId, entry);
        map.set(String(parseInt(norm.numericId, 10)), entry);
      }
      if (norm.iso2) map.set(norm.iso2.toUpperCase(), entry);
      if (norm.iso3) map.set(norm.iso3.toUpperCase(), entry);
      if (n.code) map.set(n.code.toUpperCase(), entry);
      if (n.country) map.set(n.country.toUpperCase(), entry);
      if (norm.name) map.set(norm.name.toUpperCase(), entry);
    }
    return map;
  }, [nations]);

  const handleCountryClick = async (countryName: string) => {
    setSelectedCountryInfo({
      countryName,
      list: [],
      loading: true
    });
    try {
      const res = await fetchNationAttacks(countryName, timeframe);
      setSelectedCountryInfo({
        countryName,
        list: res.attacks || [],
        loading: false
      });
    } catch (err) {
      console.error("Failed to load country attacks", err);
      setSelectedCountryInfo({
        countryName,
        list: [],
        loading: false,
        error: true
      });
    }
  };

  useEffect(() => {
    clickHandlerRef.current = handleCountryClick;
  });

  // Calculate dynamic centers from GeoJSON
  useEffect(() => {
    const centers: Record<string, [number, number]> = {};
    if (countriesGeoJson && countriesGeoJson.features) {
      (countriesGeoJson.features as any[]).forEach((feature: any) => {
        const numId = String(feature.id).padStart(3, "0");
        const geoName = feature.properties?.name;
        if (!geoName) return;
        
        // Only calculate center if the country is in our data
        const norm = normalizeCountry(geoName, numId);
        const nation =
          nationMap.get(numId) ||
          nationMap.get(String(parseInt(numId, 10))) ||
          nationMap.get(norm.iso2) ||
          nationMap.get(norm.iso3) ||
          nationMap.get(geoName.toUpperCase());

        if (!nation) return;

        try {
          const layer = L.geoJSON(feature);
          const center = layer.getBounds().getCenter();
          if (typeof center.lat === 'number' && typeof center.lng === 'number' && !isNaN(center.lat) && !isNaN(center.lng)) {
            centers[norm.name] = [center.lat, center.lng];
          }
        } catch (err) {}
      });
    }
    setGeoJsonCenters(centers);
  }, [nationMap]);

  // Initialize Map
  useEffect(() => {
    if (!mapContainerRef.current) return;
    const worldBounds = L.latLngBounds([-85, -180], [85, 180]);
    const mapInstance = L.map(mapContainerRef.current, {
      center: [20, 0],
      zoom: 1, // Safe initial value
      zoomSnap: 0.25,
      zoomDelta: 0.5,
      maxZoom: 18,
      zoomControl: false,
      attributionControl: false,
      renderer: L.canvas(),
      maxBounds: worldBounds,
      maxBoundsViscosity: 1.0,
      worldCopyJump: false
    });
    
    L.control.zoom({ position: 'bottomright' }).addTo(mapInstance);
    
    mapInstance.createPane('labelsPane');
    const labelsPane = mapInstance.getPane('labelsPane');
    if (labelsPane) {
      labelsPane.style.zIndex = '450';
      labelsPane.style.pointerEvents = 'none';
    }
    setMap(mapInstance);
    
    const updateMinZoom = () => {
      mapInstance.invalidateSize();
      const minZ = mapInstance.getBoundsZoom(worldBounds, true);
      mapInstance.setMinZoom(minZ);
      if (mapInstance.getZoom() < minZ) {
        mapInstance.setView([20, 0], minZ, { animate: false });
      }
    };

    setTimeout(() => {
      updateMinZoom();
    }, 100);

    const resizeObserver = new ResizeObserver(() => {
      updateMinZoom();
    });
    resizeObserver.observe(mapContainerRef.current);

    return () => {
      resizeObserver.disconnect();
      mapInstance.remove();
      setMap(null);
    };
  }, []);

  // Base Tiles
  useEffect(() => {
    if (!map) return;
    map.eachLayer((layer) => {
      if (layer instanceof L.TileLayer) {
        map.removeLayer(layer);
      }
    });
    
    const worldBounds = L.latLngBounds([-85, -180], [85, 180]);
    const baseTileUrl = isDark
      ? 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}'
      : 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';
    const refTileUrl = isDark
      ? 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}'
      : 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}';
      
    const baseLayer = L.tileLayer(baseTileUrl, {
      maxZoom: 18,
      maxNativeZoom: 16,
      noWrap: true,
      bounds: worldBounds
    });
    const refLayer = L.tileLayer(refTileUrl, {
      maxZoom: 18,
      maxNativeZoom: 16,
      pane: 'labelsPane',
      noWrap: true,
      bounds: worldBounds
    });
    
    baseLayer.addTo(map);
    refLayer.addTo(map);
  }, [map, isDark]);

  const nationMapRef = useRef(nationMap);
  useEffect(() => {
    nationMapRef.current = nationMap;
  }, [nationMap]);

  // GeoJSON Fills
  useEffect(() => {
    if (!map) return;

    const getStyleForFeature = (feature: any, isDarkTheme: boolean, maxCnt: number) => {
      const numId = String(feature.id).padStart(3, "0");
      const geoName = feature.properties?.name || "Unknown";
      const norm = normalizeCountry(geoName, numId);
      const nation =
        nationMapRef.current.get(numId) ||
        nationMapRef.current.get(String(parseInt(numId, 10))) ||
        nationMapRef.current.get(norm.iso2) ||
        nationMapRef.current.get(norm.iso3) ||
        nationMapRef.current.get(geoName.toUpperCase());

      const count = nation ? nation.count : 0;
      if (count > 0) {
        const markerColor = getCountryMarkerColor(count, maxCnt);
        return {
          fillColor: markerColor,
          fillOpacity: isDarkTheme ? 0.25 : 0.20,
          color: isDarkTheme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
          weight: 1,
          className: 'leaflet-country-path'
        };
      } else {
        return {
          fillColor: isDarkTheme ? '#1e293b' : '#f1f5f9',
          fillOpacity: isDarkTheme ? 0.3 : 1.0,
          color: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
          weight: 0.8,
          className: 'leaflet-country-path'
        };
      }
    };

    if (!geoJsonLayerRef.current) {
      const geoJsonLayer = L.geoJSON(countriesGeoJson as any, {
        style: (feature: any) => getStyleForFeature(feature, isDark, maxCount),
        onEachFeature: (feature: any, layer: any) => {
          layer.on({
            mouseover: (e: any) => {
              const l = e.target;
              l.setStyle({
                fillOpacity: Math.min((l.options.fillOpacity || 0) + 0.15, 0.85),
                weight: 1.5,
                color: isDark ? '#ffffff50' : '#00000050'
              });
              l.bringToFront();
            },
            mouseout: (e: any) => {
              e.target.setStyle(getStyleForFeature(feature, isDark, maxCount));
            },
            click: () => {
              const numId = String(feature.id).padStart(3, "0");
              const geoName = feature.properties?.name || "Unknown";
              const norm = normalizeCountry(geoName, numId);
              const nation =
                nationMapRef.current.get(numId) ||
                nationMapRef.current.get(String(parseInt(numId, 10))) ||
                nationMapRef.current.get(norm.iso2) ||
                nationMapRef.current.get(norm.iso3) ||
                nationMapRef.current.get(geoName.toUpperCase());

              if (nation && nation.count > 0) {
                clickHandlerRef.current(nation.country);
              }
            }
          });
        }
      });
      geoJsonLayer.addTo(map);
      geoJsonLayerRef.current = geoJsonLayer as any;
    }

    // Update existing geoJson layer in-place
    geoJsonLayerRef.current.eachLayer((layer: any) => {
      const feature = layer.feature;
      layer.setStyle(getStyleForFeature(feature, isDark, maxCount));
      
      const numId = String(feature.id).padStart(3, "0");
      const geoName = feature.properties?.name || "Unknown";
      const norm = normalizeCountry(geoName, numId);
      const nation =
        nationMap.get(numId) ||
        nationMap.get(String(parseInt(numId, 10))) ||
        nationMap.get(norm.iso2) ||
        nationMap.get(norm.iso3) ||
        nationMap.get(geoName.toUpperCase());

      const count = nation ? nation.count : 0;
      if (count > 0 && nation) {
        const safeName = nation.country.replace(/</g, "&lt;").replace(/>/g, "&gt;");
        const tooltipContent = `<strong>${safeName}</strong>: ${count} Attacks`;
        if (layer.getTooltip()) {
          layer.setTooltipContent(tooltipContent);
        } else {
          layer.bindTooltip(tooltipContent, {
            sticky: true,
            direction: 'auto',
            className: 'custom-map-tooltip'
          });
        }
      } else {
        if (layer.getTooltip()) {
          layer.unbindTooltip();
        }
      }
      
      // Update mouseout listener closure for isDark
      layer.off('mouseout');
      layer.on('mouseout', (e: any) => {
        e.target.setStyle(getStyleForFeature(feature, isDark, maxCount));
      });
      layer.off('mouseover');
      layer.on('mouseover', (e: any) => {
        const l = e.target;
        l.setStyle({
          fillOpacity: Math.min((l.options.fillOpacity || 0) + 0.15, 0.85),
          weight: 1.5,
          color: isDark ? '#ffffff50' : '#00000050'
        });
        l.bringToFront();
      });
    });
  }, [map, nationMap, maxCount, isDark]);

  // Markers map ref for diffing
  const activeMarkersRef = useRef<Map<string, L.Marker>>(new Map());

  // Markers
  useEffect(() => {
    if (!map) return;
    if (!markersGroupRef.current) {
      markersGroupRef.current = L.featureGroup().addTo(map);
    }
    
    const activeMarkers = activeMarkersRef.current;
    const currentCountries = new Set<string>();

    nations.forEach((nation) => {
      const countryName = nation.country;
      currentCountries.add(countryName);
      const baseCoords = geoJsonCenters[countryName] || fallbackCoords[countryName];
      if (!baseCoords) return;

      const count = nation.count;
      const dotColor = getCountryMarkerColor(count, maxCount);
      
      const safeName = countryName.replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const actorsHtml = (nation.top_actors || [])
        .map((actor: string) => {
          const safeActor = actor.replace(/</g, "&lt;").replace(/>/g, "&gt;");
          const color = getActorColor(safeActor);
          return `<div class="flex items-center gap-1.5 mt-0.5 text-[9px]">
            <span class="w-1.5 h-1.5 rounded-full" style="background-color: ${color}"></span>
            <span class="text-slate-300 font-medium">${safeActor}</span>
          </div>`;
        }).join('');
        
      const tooltipHtml = `
        <div class="font-sans text-[10px] p-1.5 leading-normal min-w-[120px]">
          <div class="font-bold text-white border-b border-white/10 pb-1 mb-1">
            ${safeName}
          </div>
          <div class="text-[10px] text-slate-300">
            Total Attacks: <strong class="text-white">${count}</strong>
          </div>
          ${actorsHtml ? `
          <div class="mt-1.5">
            <div class="text-[8px] font-bold uppercase tracking-wide text-slate-500">Top Actors</div>
            ${actorsHtml}
          </div>
          ` : ''}
        </div>
      `;

      if (activeMarkers.has(countryName)) {
        // Update existing marker
        const marker = activeMarkers.get(countryName)!;
        marker.setTooltipContent(tooltipHtml);
        
        // Only update icon if color changed to prevent animation reset
        const currentIconHtml = (marker.getIcon() as any).options.html;
        if (!currentIconHtml.includes(`style="color: ${dotColor};"`)) {
          const newIconHtml = `
            <div class="glowing-dot-container" style="color: ${dotColor};">
              <div class="glowing-dot-ring"></div>
              <div class="glowing-dot-center"></div>
            </div>
          `;
          marker.setIcon(L.divIcon({
            html: newIconHtml,
            className: 'custom-glowing-marker',
            iconSize: [24, 24],
            iconAnchor: [12, 12]
          }));
        }
      } else {
        // Create new marker
        const iconHtml = `
          <div class="glowing-dot-container" style="color: ${dotColor};">
            <div class="glowing-dot-ring"></div>
            <div class="glowing-dot-center"></div>
          </div>
        `;
        const markerIcon = L.divIcon({
          html: iconHtml,
          className: 'custom-glowing-marker',
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        });
        
        const marker = L.marker(baseCoords, { icon: markerIcon });
        marker.bindTooltip(tooltipHtml, {
          direction: 'top',
          offset: [0, -8],
          className: 'custom-map-tooltip'
        });
        
        marker.on('click', () => {
          clickHandlerRef.current(countryName);
        });
        marker.addTo(markersGroupRef.current!);
        activeMarkers.set(countryName, marker);
      }
    });

    // Remove markers that are no longer in the data
    for (const [countryName, marker] of activeMarkers.entries()) {
      if (!currentCountries.has(countryName)) {
        markersGroupRef.current!.removeLayer(marker);
        activeMarkers.delete(countryName);
      }
    }
  }, [map, nations, geoJsonCenters, maxCount]);

  const modalRoot = document.body;

  return (
    <div className={`tx-threat-map ${isDark ? 'dark' : ''} bg-slate-50 dark:bg-[#0b1329]`}>
      <div ref={mapContainerRef} className="w-full h-full min-h-[480px]" />
      
      {/* Attribution */}
      <div className="absolute bottom-1 left-2 z-[400] text-[10px] text-slate-500 pointer-events-none opacity-60 bg-white/50 dark:bg-black/50 px-1 rounded">
        Tiles © Esri — Source: Esri, DeLorme, NAVTEQ
      </div>

      {/* Floating Panel */}
      <div 
        className={`absolute top-4 left-4 z-[1000] rounded-xl transition-all duration-500 ease-in-out border border-slate-200 dark:border-white/10 backdrop-blur-xl shadow-[0_8px_32px_0_rgba(0,0,0,0.12)] dark:shadow-[0_8px_32px_0_rgba(0,0,0,0.45)] ${
          isCollapsed 
            ? 'w-11 h-11 bg-white/50 dark:bg-slate-950/30 flex items-center justify-center hover:bg-white/80 dark:hover:bg-slate-900/50' 
            : 'w-80 max-h-[380px] bg-white/80 dark:bg-slate-950/50 p-4 flex flex-col'
        }`}
      >
        {isCollapsed ? (
          <button 
            onClick={() => setIsCollapsed(false)} 
            className="w-full h-full flex items-center justify-center text-black dark:text-white transition-colors cursor-pointer"
            title="Expand Attack Distribution"
          >
            <List className="w-6 h-6" />
          </button>
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-white/10 pb-2.5 mb-3 shrink-0">
              <span className="text-[11px] font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2 uppercase tracking-widest">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#6366F1] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#6366F1]"></span>
                </span>
                Attack Distribution
              </span>
              <button 
                onClick={() => setIsCollapsed(true)} 
                className="p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-lg transition-colors text-black dark:text-white cursor-pointer"
                title="Collapse Panel"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            </div>
            
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-xs text-slate-500 dark:text-slate-400 py-6">
                Loading statistics...
              </div>
            ) : nations.length > 0 ? (
              <div className="space-y-2.5 overflow-y-auto liquid-scroll flex-1 pr-1 mb-3">
                {nations.map((c) => {
                  const percentage = totalAttacks > 0 ? (c.count / totalAttacks) * 100 : 0;
                  const themeColor = isDark ? '#6366F1' : '#6366F1';
                  return (
                    <div 
                      key={c.country} 
                      onClick={() => handleCountryClick(c.country)}
                      className="flex flex-col gap-1 group cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 p-1.5 rounded-lg transition-all"
                    >
                      <div className="flex justify-between items-center text-[10px] font-bold text-slate-800 dark:text-slate-300">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full ring-2 ring-white dark:ring-slate-950 animate-pulse" style={{ backgroundColor: themeColor, boxShadow: `0 0 8px ${themeColor}` }}></span>
                          <span className="group-hover:text-black dark:group-hover:text-white transition-colors">{c.country}</span>
                        </div>
                        <span className="font-mono text-slate-900 dark:text-white bg-black/5 dark:bg-white/10 px-1.5 py-[2px] rounded text-[9px]">
                          {c.count.toLocaleString()}
                        </span>
                      </div>
                      <div className="w-full bg-slate-200/50 dark:bg-white/10 h-1 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500" 
                          style={{ 
                            width: `${percentage}%`,
                            backgroundColor: themeColor,
                            boxShadow: `0 0 6px ${themeColor}` 
                          }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-xs text-slate-500 dark:text-slate-400 py-6">
                No active threats
              </div>
            )}
            
            <div className="border-t border-slate-200 dark:border-white/10 pt-3 shrink-0">
              <div className="text-[8px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                Threat Severity Legend
              </div>
              <div className="grid grid-cols-3 gap-1">
                <div className="flex items-center gap-1.5 bg-red-50 dark:bg-red-500/5 border border-red-500/20 px-1.5 py-1 rounded">
                  <span className="w-2 h-2 rounded-full bg-[#ef4444] animate-pulse shrink-0"></span>
                  <div className="flex flex-col min-w-0">
                    <span className="text-[8px] font-bold text-red-600 dark:text-red-400 truncate">High</span>
                    <span className="text-[7px] text-slate-500 dark:text-slate-400 font-mono">&gt;{highThreshold}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 bg-yellow-50 dark:bg-yellow-500/5 border border-yellow-500/20 px-1.5 py-1 rounded">
                  <span className="w-2 h-2 rounded-full bg-[#fbbf24] animate-pulse shrink-0"></span>
                  <div className="flex flex-col min-w-0">
                    <span className="text-[8px] font-bold text-yellow-600 dark:text-yellow-500 truncate">Medium</span>
                    <span className="text-[7px] text-slate-500 dark:text-slate-400 font-mono">{medThreshold}-{highThreshold}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-500/5 border border-blue-500/20 px-1.5 py-1 rounded">
                  <span className="w-2 h-2 rounded-full bg-[#3b82f6] animate-pulse shrink-0"></span>
                  <div className="flex flex-col min-w-0">
                    <span className="text-[8px] font-bold text-blue-600 dark:text-blue-400 truncate">Low</span>
                    <span className="text-[7px] text-slate-500 dark:text-slate-400 font-mono">&lt;{medThreshold}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals via Portal */}
      {selectedCountryInfo && createPortal(
        <div className={`fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm tx-fade-in ${isDark ? 'dark' : ''}`}>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded shadow-2xl max-w-2xl w-full overflow-hidden tx-zoom-in flex flex-col max-h-[85vh]">
            <div className="px-5 py-3.5 bg-slate-100 dark:bg-[#001433] flex justify-between items-center text-slate-800 dark:text-white shrink-0">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#6366F1] dark:bg-[#6366F1] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#6366F1] dark:bg-[#6366F1]"></span>
                </span>
                <span className="font-semibold text-xs uppercase tracking-wider">
                  Attack Activity: {selectedCountryInfo.countryName}
                </span>
                <span className="px-1.5 py-0.5 bg-slate-200 dark:bg-white/15 rounded text-[10px] font-bold text-slate-700 dark:text-white">
                  {nationMap.get(selectedCountryInfo.countryName.toUpperCase())?.count || 0} Attacks
                </span>
              </div>
              <button 
                onClick={() => setSelectedCountryInfo(null)} 
                className="text-slate-500 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto liquid-scroll flex-1 space-y-3 bg-slate-50 dark:bg-slate-950/20 min-h-[200px] flex flex-col justify-start">
              {selectedCountryInfo.loading ? (
                <div className="flex-grow flex flex-col items-center justify-center py-12 gap-3 text-slate-500 dark:text-slate-400">
                  <div className="w-8 h-8 border-[3px] border-[#6366F1] dark:border-[#6366F1] border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs font-semibold uppercase tracking-wider animate-pulse">Loading intelligence...</span>
                </div>
              ) : selectedCountryInfo.error ? (
                <div className="flex-grow flex flex-col items-center justify-center py-12 gap-2 text-red-500 text-xs">
                  <AlertCircle className="w-8 h-8 mb-1" />
                  <span className="font-bold">Failed to load telemetry</span>
                  <span className="text-[10px] text-slate-500 dark:text-slate-400">Please try again later.</span>
                </div>
              ) : selectedCountryInfo.list.length === 0 ? (
                <div className="flex-grow flex flex-col items-center justify-center py-12 text-slate-500 dark:text-slate-400 text-xs gap-1.5">
                  <ShieldCheck className="w-8 h-8 text-emerald-500 mb-1" />
                  <span className="font-bold text-slate-700 dark:text-slate-300">No active threats detected</span>
                  <span className="text-[10px] text-slate-500 dark:text-slate-400">This region is currently clear.</span>
                </div>
              ) : (
                selectedCountryInfo.list.map((attack) => {
                  const actorColor = getActorColor(attack.actor);
                  return (
                    <div 
                      key={attack.id}
                      onClick={() => setSelectedAttack(attack)}
                      className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded shadow-sm hover:border-sky-500/50 dark:hover:border-[#6366F1]/50 hover:shadow-md transition-all cursor-pointer flex items-center justify-between group"
                    >
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-800 dark:text-slate-100 group-hover:text-[#6366F1] dark:group-hover:text-[#6366F1] transition-colors">
                            {attack.victim}
                          </span>
                          <span 
                            className="px-1.5 py-[2px] rounded text-[8px] font-bold uppercase tracking-wide border"
                            style={{ 
                              color: actorColor,
                              borderColor: `${actorColor}30`,
                              backgroundColor: `${actorColor}10`
                            }}
                          >
                            {attack.actor}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-slate-500 dark:text-slate-400">
                          <span>Sector: <strong className="text-slate-700 dark:text-slate-300">{attack.sector}</strong></span>
                          <span>Date: <strong className="text-slate-700 dark:text-slate-300">{new Date(attack.date).toLocaleDateString()}</strong></span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-[#6366F1] dark:text-[#6366F1] opacity-80 group-hover:opacity-100 transition-opacity">
                        <span>View details</span>
                        <ArrowRight className="w-3 h-3 transform group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  );
                }))}
            </div>
            <div className="bg-slate-100 dark:bg-slate-950/40 px-5 py-3 border-t border-slate-200 dark:border-white/10 flex justify-end shrink-0">
              <button 
                onClick={() => setSelectedCountryInfo(null)}
                className="px-3.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-white/15 hover:bg-slate-50 dark:hover:bg-slate-800 rounded text-xs font-semibold text-slate-700 dark:text-slate-300 transition-colors cursor-pointer"
              >
                Close List
              </button>
            </div>
          </div>
        </div>, modalRoot
      )}

      {selectedAttack && createPortal(
        <div className={`fixed inset-0 z-[2100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm tx-fade-in ${isDark ? 'dark' : ''}`}>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded shadow-2xl max-w-md w-full overflow-hidden tx-zoom-in">
            <div className="px-5 py-3.5 bg-slate-100 dark:bg-[#001433] flex justify-between items-center text-slate-800 dark:text-white">
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setSelectedAttack(null)} 
                  className="mr-2 text-slate-500 hover:text-slate-800 dark:text-slate-300 dark:hover:text-[#6366F1] transition-colors cursor-pointer flex items-center gap-1 text-[11px] font-bold"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back</span>
                </button>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                <span className="font-semibold text-xs uppercase tracking-wider">Attack Details</span>
              </div>
              <button 
                onClick={() => {
                  setSelectedAttack(null);
                  setSelectedCountryInfo(null);
                }} 
                className="text-slate-500 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4 max-h-[480px] overflow-y-auto liquid-scroll">
              <div className="grid grid-cols-2 gap-4 border-b border-slate-200 dark:border-white/10 pb-4">
                <div>
                  <span className="text-[9px] uppercase font-bold text-slate-500 dark:text-slate-400 tracking-wider">Victim / Target</span>
                  <p className="text-sm font-bold text-slate-800 dark:text-slate-100 mt-0.5">{selectedAttack.victim}</p>
                </div>
                <div>
                  <span className="text-[9px] uppercase font-bold text-slate-500 dark:text-slate-400 tracking-wider">Threat Actor</span>
                  <p className="mt-0.5">
                    <span 
                      className="px-2 py-[2px] rounded text-[9px] font-bold uppercase tracking-wide border"
                      style={{ 
                        color: getActorColor(selectedAttack.actor),
                        borderColor: `${getActorColor(selectedAttack.actor)}30`,
                        backgroundColor: `${getActorColor(selectedAttack.actor)}10`
                      }}
                    >
                      {selectedAttack.actor}
                    </span>
                  </p>
                </div>
                <div>
                  <span className="text-[9px] uppercase font-bold text-slate-500 dark:text-slate-400 tracking-wider">Sector</span>
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-0.5">{selectedAttack.sector}</p>
                </div>
                <div>
                  <span className="text-[9px] uppercase font-bold text-slate-500 dark:text-slate-400 tracking-wider">Date</span>
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-0.5">
                    {new Date(selectedAttack.date).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div>
                <span className="text-[9px] uppercase font-bold text-slate-500 dark:text-slate-400 tracking-wider block mb-1.5">Description / Context</span>
                <div className="p-3 bg-slate-50 dark:bg-slate-950 rounded border border-slate-200 dark:border-white/5 text-[11px] text-slate-700 dark:text-slate-300 max-h-40 overflow-y-auto liquid-scroll whitespace-pre-wrap">
                  {selectedAttack.description || "No detailed description available."}
                </div>
              </div>
            </div>
            <div className="bg-slate-100 dark:bg-slate-950/40 px-5 py-3 border-t border-slate-200 dark:border-white/10 flex justify-end gap-2">
              <button 
                onClick={() => setSelectedAttack(null)}
                className="px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-white/15 hover:bg-slate-50 dark:hover:bg-slate-800 rounded text-xs font-semibold text-slate-700 dark:text-slate-300 transition-colors cursor-pointer flex items-center gap-1"
              >
                <ArrowLeft className="w-3 h-3" />
                Back to List
              </button>
              <button 
                onClick={() => {
                  setSelectedAttack(null);
                  setSelectedCountryInfo(null);
                }}
                className="px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-white/15 hover:bg-slate-50 dark:hover:bg-slate-800 rounded text-xs font-semibold text-slate-700 dark:text-slate-300 transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>, modalRoot
      )}
    </div>
  );
}

